import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import * as Linking from 'expo-linking';
import { Platform, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import DeviceInfo from 'react-native-device-info';

// Configure how notifications are handled when app is in foreground
Notifications.setNotificationHandler({
  handleNotification: async (): Promise<Notifications.NotificationBehavior> => ({
    shouldShowAlert: false,  // Don't show native alert in foreground
    shouldPlaySound: false,  // Don't play sound in foreground (toast handles UI)
    shouldSetBadge: true,    // Still update badge
    // iOS 15+ presentation hints: disable banners/lists in foreground
    shouldShowBanner: false,
    shouldShowList: false,
  }),
});

export interface NotificationData {
  type?: string;
  id?: string;
  screen?: string;  // For new backend format: { type: "navigate", screen: "ChargeDetail", params: {...} }
  params?: any;     // Navigation parameters
  deepLink?: string; // For legacy deep link support
  [key: string]: any;
}

export interface NotificationPayload {
  title?: string;
  body?: string;
  data?: NotificationData;
  sound?: string;
  badge?: number;
  ttl?: number;
}

export interface TokenRegistrationData {
  token: string;
  platform: 'ios' | 'android';
  userId: string;
  deviceId?: string;
}

class ExpoNotificationService {
  private static readonly STORAGE_KEY = 'expo_push_token';
  private static readonly FCM_TOKEN_KEY = 'fcm_device_token';
  private static readonly LAST_REGISTRATION_KEY = 'last_token_registration';
  
  private notificationListener?: Notifications.EventSubscription;
  private responseListener?: Notifications.EventSubscription;
  private tokenRefreshListener?: Notifications.EventSubscription;
  private foregroundHandler?: (notification: Notifications.Notification) => void;
  private backgroundHandler?: (response: Notifications.NotificationResponse) => void;
  private isRegistering = false; // Prevent concurrent registrations
  private lastRegisteredToken: string | null = null; // Track last registered token

  /**
   * Request notification permissions from the user
   */
  async requestPermissions(): Promise<boolean> {
    try {
      if (!Device.isDevice) {
        console.warn('Push notifications only work on physical devices');
        //return false;
      }

      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      let finalStatus = existingStatus;

      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      if (finalStatus !== 'granted') {
        console.log('Push notification permission denied');
        Alert.alert(
          'Notifications Disabled',
          'To receive important updates about your charging sessions, please enable notifications in Settings.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Open Settings', onPress: () => Linking.openSettings() }
          ]
        );
        return false;
      }

      console.log('Push notification permission granted');
      return true;
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
      return false;
    }
  }

  /**
   * Get Expo push token and FCM/APNs token if available
   */
  async getAndRegisterToken(providedUserId?: string): Promise<{ expoPushToken?: string; deviceToken?: string }> {
    try {
      if (!Device.isDevice) {
        return {};
      }

      // Prevent concurrent registrations
      if (this.isRegistering) {
        return {};
      }

      this.isRegistering = true;

      // Get user ID from AsyncStorage or use provided one
      let userId = providedUserId;
      if (!userId) {
        userId = await AsyncStorage.getItem('userId') || undefined;
      }
      
      // Check if user is logged in and not a guest
      const userRole = await AsyncStorage.getItem('userRole');
      
      if (!userId || userRole === 'guest') {
        return {};
      }

      // Get Expo push token
      const expoPushToken = await this.getExpoPushToken();
      
      // Try to get the native device token (FCM/APNs)
      const deviceToken = await this.getDeviceToken();

      if (expoPushToken || deviceToken) {
        const tokenToRegister = deviceToken || expoPushToken;
        if (tokenToRegister) {
          
          // Additional check: don't register the same token again immediately
          if (this.lastRegisteredToken === tokenToRegister) {
            // console.log('Token identical to last registered token, skipping');
            return { expoPushToken, deviceToken };
          }

          // Check if we need to register (don't validate, just register directly)
          const lastRegistration = await AsyncStorage.getItem(ExpoNotificationService.LAST_REGISTRATION_KEY);
          const lastRegistrationData = lastRegistration ? JSON.parse(lastRegistration) : null;
          
          let needsRegistration = !lastRegistrationData || 
            lastRegistrationData.token !== tokenToRegister ||
            lastRegistrationData.userId !== userId ||
            (Date.now() - lastRegistrationData.timestamp) > 24 * 60 * 60 * 1000; // Re-register after 24 hours

          // If local cache says no registration needed, verify presence on backend
          if (!needsRegistration) {
            try {
              const existsOnBackend = await this.validateTokenWithBackend(tokenToRegister);
              if (!existsOnBackend) {
                needsRegistration = true;
                await AsyncStorage.removeItem(ExpoNotificationService.LAST_REGISTRATION_KEY);
              }
            } catch (e) {
              // If validation fails due to network, keep existing behavior (do not force)
              console.warn('Backend validation skipped due to error:', e);
            }
          }

          if (needsRegistration) {
            // console.log('Registering push token for authenticated user:', userId);

            // Get device unique ID (IDFV on iOS, ANDROID_ID on Android)
            const deviceUniqueId = await DeviceInfo.getUniqueId().catch(() => 
              Constants.installationId || Device.modelName || 'unknown'
            );
            
            // Register token with backend (no validate call as requested)
            await this.registerTokenWithBackend({
              token: tokenToRegister,
              platform: Platform.OS as 'ios' | 'android',
              userId,
              deviceId: deviceUniqueId
            });
          }
        }

        // Store tokens locally
        if (expoPushToken) {
          await AsyncStorage.setItem(ExpoNotificationService.STORAGE_KEY, expoPushToken);
        }
        if (deviceToken) {
          await AsyncStorage.setItem(ExpoNotificationService.FCM_TOKEN_KEY, deviceToken);
        }
      }

      return { expoPushToken, deviceToken };
    } catch (error) {
      console.error('Error getting push tokens:', error);
      return {};
    } finally {
      this.isRegistering = false;
    }
  }

  /**
   * Get Expo push token (kept for backward compatibility)
   */
  private async getExpoPushToken(): Promise<string | undefined> {
    try {
      const projectId = Constants.expoConfig?.extra?.eas?.projectId || Constants.easConfig?.projectId;
      
      if (!projectId) {
        console.warn('No project ID found for Expo push notifications');
        return undefined;
      }

      const { data: token } = await Notifications.getExpoPushTokenAsync({
        projectId,
      });

      // console.log('Expo Push Token:', token);
      return token;
    } catch (error) {
      console.error('Error getting Expo push token:', error);
      return undefined;
    }
  }

  /**
   * Get native device token (FCM for Android, APNs for iOS)
   */
  private async getDeviceToken(): Promise<string | undefined> {
    try {
      const { data: token } = await Notifications.getDevicePushTokenAsync();
      // console.log('Device Push Token:', token);
      return token;
    } catch (error) {
      console.error('Error getting device push token:', error);
      return undefined;
    }
  }

  /**
   * Register token with backend using new endpoint with retry logic
   */
  private async registerTokenWithBackend(data: TokenRegistrationData): Promise<void> {
    const maxRetries = 3;
    let attempt = 1;
    
    while (attempt <= maxRetries) {
      try {
        const url = `${process.env.REACT_NATIVE_API_BASE_URL}push/register`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        } as any);

        const responseBody = await response.json().catch(() => ({}));

        if (!response.ok) {
          // Handle specific error cases
          if (response.status === 400) {
            const errorMessage = (responseBody as any).message || (responseBody as any).error || 'Bad request';
            console.error('Registration error (400):', errorMessage);
            // Don't retry other 400 errors
            throw new Error(`Client error: ${errorMessage}`);
          }

          // Retry on server errors (5xx) with exponential backoff
          if (response.status >= 500 && attempt < maxRetries) {
            const delay = 1000 * Math.pow(2, attempt - 1); // 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, delay));
            attempt++;
            continue;
          }

          throw new Error(`Backend registration failed: ${response.status} ${response.statusText}`);
        }

        return;
      } catch (error: any) {
        
        // Retry on network/timeout errors
        if (attempt < maxRetries && !error.message.includes('Client error')) {
          const delay = 1000 * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
          attempt++;
          continue;
        }
        return;
      }
    }
  }

  /**
   * Validate token with backend (for debugging purposes only)
   */
  private async validateTokenWithBackend(deviceToken: string): Promise<boolean> {
    try {
      const url = `${process.env.REACT_NATIVE_API_BASE_URL}push/validate`;
      
      const platform = Platform.OS === 'ios' ? 'ios' : 'android';

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          deviceToken,
          platform 
        }),
        timeout: 5000,
      } as any);

      if (!response.ok) {
        console.warn('Token validation failed:', response.status);
        return false;
      }

      const result = await response.json();
      return (result as any).success && (result as any).isValid;
    } catch (error: any) {
      console.warn('Token validation error:', error.message);
      return false;
    }
  }

  /**
   * Set up automatic token refresh handling
   */
  setupTokenRefreshHandling(): void {
    try {
      let lastToken: string | null = null;
      
      // Listen for Expo push token changes
      this.tokenRefreshListener = Notifications.addPushTokenListener(async (event) => {
        const newToken = event.data;
        
        // Only process if token actually changed
        if (newToken && newToken !== lastToken) {
          console.log('Push token actually changed:', newToken ? `${newToken.substring(0, 20)}...` : 'null');
          lastToken = newToken;
          
          // Get current user from AsyncStorage
          const userId = await AsyncStorage.getItem('userId');
          const userRole = await AsyncStorage.getItem('userRole');
          
          if (userId && userRole !== 'guest') {
            console.log('Token changed, re-registering for user:', userId);
            // Clear the registration cache to force re-registration
            await AsyncStorage.removeItem(ExpoNotificationService.LAST_REGISTRATION_KEY);
            // Re-register with new token
            await this.getAndRegisterToken(userId);
          } else {
            console.log('Token changed but no authenticated user, skipping re-registration');
          }
        } else {
          console.log('Push token listener fired but token unchanged, skipping');
        }
      });
      
      console.log('Token refresh listener set up');
    } catch (error) {
      console.error('Error setting up token refresh handling:', JSON.stringify(error));
    }
  }

  /**
   * Set up notification listeners
   */
  setupNotificationListeners(): void {
    // Listen for notifications received while app is in foreground
    this.notificationListener = Notifications.addNotificationReceivedListener(
      this.handleForegroundNotification.bind(this)
    );

    // Listen for user tapping on notifications (background/quit state)
    this.responseListener = Notifications.addNotificationResponseReceivedListener(
      this.handleNotificationResponse.bind(this)
    );

    console.log('Notification listeners set up');
  }

  /**
   * Handle notifications received in foreground
   */
  private handleForegroundNotification(notification: Notifications.Notification): void {
    console.log('Foreground notification received:', notification);
    
    // Call custom handler if set
    if (this.foregroundHandler) {
      this.foregroundHandler(notification);
    }
  }

  /**
   * Handle notification taps (background/quit state)
   */
  private handleNotificationResponse(response: Notifications.NotificationResponse): void {
    console.log('Notification response received:', response);
    
    const data = response.notification.request.content.data as NotificationData;
    
    // Call custom handler if set
    if (this.backgroundHandler) {
      this.backgroundHandler(response);
    } else {
      // Default navigation behavior
      this.navigateBasedOnPayload(data);
    }
  }

  /**
   * Navigate based on notification payload
   */
  private navigateBasedOnPayload(data: NotificationData): void {
    if (!data) return;

    console.log('Navigating based on payload:', data);

    // Import navigation here to avoid circular dependencies
    const RootNavigation = require('../../RootNavigation');
    
    try {
      if (data.type === 'charge_status' && data.id) {
        // Navigate to ChargeDetail screen with the charge ID
        RootNavigation.navigate('ChargeDetail', { chargeId: data.id });
      } else if (data.type === 'charger_available' && data.id) {
        // Navigate to charger details
        RootNavigation.navigate('ChargerDetails', { chargerId: data.id });
      } else if (data.deepLink) {
        // Handle custom deep links
        RootNavigation.navigate(data.deepLink, data.params || {});
      } else {
        // Default: navigate to notifications or home screen
        RootNavigation.navigate('Home');
      }
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback to home screen
      try {
        RootNavigation.navigate('Home');
      } catch (fallbackError) {
        console.error('Fallback navigation also failed:', fallbackError);
      }
    }
  }

  /**
   * Set custom foreground notification handler
   */
  onForegroundNotification(handler: (notification: Notifications.Notification) => void): void {
    this.foregroundHandler = handler;
  }

  /**
   * Set custom notification response handler (background/quit taps)
   */
  onNotificationResponse(handler: (response: Notifications.NotificationResponse) => void): void {
    this.backgroundHandler = handler;
  }

  /**
   * Check if notification was used to open the app (cold start)
   */
  async getInitialNotification(): Promise<Notifications.NotificationResponse | null> {
    try {
      const response = await Notifications.getLastNotificationResponseAsync();
      if (response) {
        console.log('App opened from notification:', response);
        // Handle the initial notification
        this.handleNotificationResponse(response);
      }
      return response;
    } catch (error) {
      console.error('Error getting initial notification:', error);
      return null;
    }
  }

  /**
   * Get stored tokens from local storage
   */
  async getStoredTokens(): Promise<{ expoPushToken?: string; deviceToken?: string }> {
    try {
      const [expoPushToken, deviceToken] = await Promise.all([
        AsyncStorage.getItem(ExpoNotificationService.STORAGE_KEY),
        AsyncStorage.getItem(ExpoNotificationService.FCM_TOKEN_KEY)
      ]);

      return {
        expoPushToken: expoPushToken || undefined,
        deviceToken: deviceToken || undefined
      };
    } catch (error) {
      console.error('Error getting stored tokens:', error);
      return {};
    }
  }

  /**
   * Refresh and re-register tokens
   */
  async refreshTokens(userId: string): Promise<void> {
    console.log('Refreshing push tokens...');
    
    try {
      // Clear stored tokens
      await AsyncStorage.multiRemove([
        ExpoNotificationService.STORAGE_KEY,
        ExpoNotificationService.FCM_TOKEN_KEY
      ]);

      // Get new tokens and register
      await this.getAndRegisterToken(userId);
    } catch (error) {
      console.error('Error refreshing tokens:', error);
    }
  }

  /**
   * Clean up listeners
   */
  cleanup(): void {
    if (this.notificationListener) {
      this.notificationListener.remove();
      this.notificationListener = undefined;
    }

    if (this.responseListener) {
      this.responseListener.remove();
      this.responseListener = undefined;
    }

    if (this.tokenRefreshListener) {
      this.tokenRefreshListener.remove();
      this.tokenRefreshListener = undefined;
    }

    console.log('Notification listeners cleaned up');
  }

  /**
   * Clear registration cache (useful for testing or logout)
   */
  async clearRegistrationCache(): Promise<void> {
    try {
      await AsyncStorage.removeItem(ExpoNotificationService.LAST_REGISTRATION_KEY);
      // console.log('Push notification registration cache cleared');
    } catch (error) {
      console.error('Error clearing registration cache:', error);
    }
  }

  /**
   * Schedule a local notification (for testing)
   */
  async scheduleLocalNotification(title: string, body: string, data?: NotificationData): Promise<string> {
    try {
      const notificationId = await Notifications.scheduleNotificationAsync({
        content: {
          title,
          body,
          data: data || {},
          sound: 'default',
        },
        trigger: null, // Show immediately
      });

      console.log('Local notification scheduled:', notificationId);
      return notificationId;
    } catch (error) {
      console.error('Error scheduling local notification:', error);
      throw error;
    }
  }

  /**
   * Cancel all scheduled notifications
   */
  async cancelAllNotifications(): Promise<void> {
    try {
      await Notifications.cancelAllScheduledNotificationsAsync();
      console.log('All scheduled notifications cancelled');
    } catch (error) {
      console.error('Error cancelling notifications:', error);
    }
  }

  /**
   * Set notification badge count (iOS)
   */
  async setBadgeCount(count: number): Promise<void> {
    try {
      await Notifications.setBadgeCountAsync(count);
      console.log(`Badge count set to: ${count}`);
    } catch (error) {
      console.error('Error setting badge count:', error);
    }
  }

  /**
   * Get current notification badge count (iOS)
   */
  async getBadgeCount(): Promise<number> {
    try {
      const count = await Notifications.getBadgeCountAsync();
      console.log(`Current badge count: ${count}`);
      return count;
    } catch (error) {
      console.error('Error getting badge count:', error);
      return 0;
    }
  }
}

export default new ExpoNotificationService();
